import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';

const MerchantDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    image_url: '',
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadDashboard();
    loadCategories();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/dashboard/merchant');
      setDashboard(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  const loadCategories = async () => {
    try {
      // Tenta buscar da nova API de categorias
      try {
        const response = await api.get('/categories/list/simple');
        setCategories(response.data);
      } catch (error) {
        // Fallback para a rota antiga
        const response = await api.get('/products/categories/list');
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleOpenDialog = () => {
    setFormData({ name: '', description: '', category: '', image_url: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    try {
      await api.post('/products', {
        ...formData,
        store_id: dashboard.store.id,
      });
      handleCloseDialog();
      loadDashboard();
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      alert('Erro ao criar produto');
    }
  };

  if (!dashboard) {
    return (
      <Container>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 6, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Box>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 700,
            }}
          >
            Dashboard - {dashboard.store.name}
          </Typography>
          {dashboard.isModerator && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'primary.main', 
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              👑 Moderador da Loja
            </Typography>
          )}
        </Box>
          <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Novo Produto
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ranking da Loja
            </Typography>
            <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700 }}>
              #{dashboard.ranking.position}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              de {dashboard.ranking.totalStores} lojas
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                Score Total: {dashboard.ranking.totalScore?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2">
                Score Médio: {dashboard.ranking.averageScore?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2">
                Produtos: {dashboard.ranking.productsCount || 0}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ranking Geral de Lojas
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Posição</TableCell>
                    <TableCell>Loja</TableCell>
                    <TableCell align="right">Score Total</TableCell>
                    <TableCell align="right">Score Médio</TableCell>
                    <TableCell align="right">Produtos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboard.storeRanking?.slice(0, 10).map((store, index) => (
                    <TableRow
                      key={store.id}
                      sx={{
                        backgroundColor: store.id === dashboard.store.id ? 'action.selected' : 'inherit',
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{store.name}</TableCell>
                      <TableCell align="right">
                        {store.total_engagement?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell align="right">
                        {store.avg_engagement?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell align="right">{store.products_count || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Meus Produtos
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Imagem</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Visualizações</TableCell>
                    <TableCell align="right">Favoritos</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboard.products?.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        {product.image_url ? (
                          <Avatar
                            src={product.image_url}
                            alt={product.name}
                            variant="rounded"
                            sx={{
                              width: 60,
                              height: 60,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}
                            component={Link}
                            to={`/product/${product.id}`}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 60,
                              height: 60,
                              backgroundColor: 'background.default',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                            component={Link}
                            to={`/product/${product.id}`}
                          >
                            <CategoryIcon sx={{ color: 'primary.main' }} />
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          component={Link}
                          to={`/product/${product.id}`}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.dark',
                            },
                          }}
                        >
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.category} 
                          size="small"
                          sx={{
                            backgroundColor: 'background.default',
                            color: 'primary.main',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{product.views_count || 0}</TableCell>
                      <TableCell align="right">{product.favorites_count || 0}</TableCell>
                      <TableCell align="right">
                        {(Number(product.engagement_score) || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Produto</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <Autocomplete
            freeSolo
            options={categories}
            value={formData.category}
            onChange={(event, newValue) => {
              setFormData({ ...formData, category: newValue || '' });
            }}
            onInputChange={(event, newInputValue) => {
              setFormData({ ...formData, category: newInputValue });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                margin="normal"
                required
                helperText="Selecione uma categoria existente ou digite uma nova"
              />
            )}
          />
          <TextField
            fullWidth
            label="URL da Imagem"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.name || !formData.category}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantDashboard;
